import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Layers, RefreshCw } from "lucide-react";
import ArbLockedGate from "../components/arbitrage/ArbLockedGate";
import ArbResults from "../components/arbitrage/ArbResults";

const SPORTS = [
  { key: "mlb", label: "MLB" },
  { key: "nba", label: "NBA" },
  { key: "nhl", label: "NHL" },
  { key: "nfl", label: "NFL" },
  { key: "mma", label: "UFC/MMA" },
];

export default function Arbitrage() {
  const [sport, setSport] = useState("mlb");
  const [stake, setStake] = useState(200);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  const load = useCallback(async (s, st) => {
    setLoading(true);
    setLocked(false);
    try {
      const res = await api.get(`/arbitrage/${s}`, { params: { stake: st } });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 402) {
        setLocked(true);
        setData(null);
      } else {
        console.warn("Could not load arbitrage", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(sport, stake);
  }, [sport, stake, load]);

  return (
    <div data-testid="arbitrage-page">
      <div className="border-b border-zinc-800 pb-10 mb-10 flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="overline mb-3 flex items-center gap-2">
            <Layers size={12} strokeWidth={1.5} /> ARB·SCANNER
          </div>
          <h1 className="font-display text-4xl lg:text-5xl">Guaranteed-profit arbs.</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            When two US books disagree hard enough that their combined implied
            probability is under 100%, you can bet both sides for a locked-in profit. We
            scan the US market live.
          </p>
        </div>
        <button
          onClick={() => load(sport, stake)}
          disabled={loading}
          data-testid="arb-refresh"
          className="flex items-center gap-2 px-4 py-2 border border-zinc-800 text-zinc-400 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono text-xs uppercase tracking-widest"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} strokeWidth={1.5} />
          )}
          Refresh
        </button>
      </div>

      {locked ? (
        <ArbLockedGate />
      ) : (
        <>
          <div className="flex flex-wrap gap-0 border-b border-zinc-800 mb-0">
            {SPORTS.map((s) => (
              <button
                key={s.key}
                data-active={sport === s.key}
                onClick={() => setSport(s.key)}
                data-testid={`arb-tab-${s.key}`}
                className="tab-terminal"
              >
                {s.label}
              </button>
            ))}
            <div className="flex-1" />
            <label className="flex items-center gap-2 px-3 text-xs mono text-zinc-500">
              TOTAL STAKE
              <input
                type="number"
                min={10}
                value={stake}
                onChange={(e) => setStake(Number(e.target.value) || 100)}
                data-testid="arb-stake"
                className="w-24 bg-transparent border border-zinc-800 px-2 py-1 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </label>
          </div>

          <div className="pt-8">
            <ArbResults data={data} loading={loading} sport={sport} />
          </div>

          <div className="mt-10 border border-zinc-800 p-5 text-xs text-zinc-400 leading-relaxed">
            <strong className="text-white">How it works:</strong> the scanner checks
            moneyline prices across all US books (DraftKings, FanDuel, BetMGM, Caesars,
            etc.) and flags any matchup where the BEST price on Team A plus the BEST
            price on Team B implies a total probability below 100%. Place both bets at
            the specified books and you collect the same return regardless of who wins.
            <span className="block mt-2 amber-text">
              ⚠ Lines move fast. Lock both legs within a couple of minutes or the arb
              vanishes.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
